import { useTranslation } from 'react-i18next';
import './Acronyms.scss';
import AcronymsList from './Acronyms_List.json';
import AcBackToTopButton from '../../components/AcBackToTopButton/AcBackToTopButton';
import SupportPageHelpSection from '../../components/HowToSync/SupportPageHelpSection';
import {
  DataGridPro,
  GridColDef,
  GridPagination,
  GridPaginationModel
} from '@mui/x-data-grid-pro';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { initializeState, showAllRows } from '../../helpers/viewAllHelper';
import { AcronymsInterface } from '../../models/AcronymsInterface';
import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { faqAutocomplete } from '../../assets/style/material/matSyles';
import {
  checkForAlphabeticChars,
  replaceSpecialChars
} from '../../helpers/commonHelpers';
import { emphasizeMatch, sanitizeHtml } from '../../helpers/sanitizeHtml';

function Acronyms() {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const [viewAllClicked, setViewAllClicked] = useState(false);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([25, 50, 100]);
  const { t } = useTranslation();
  const [acronymsList, setAcronymsList] =
    useState<AcronymsInterface[]>(AcronymsList);
  const [selectedLetter, setSelectedLetter] = useState<string>('all');
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteStyle = faqAutocomplete();
  const [autocompleteText, setAutocompleteText] = useState('');
  const [options, setOptions] = useState<AcronymsInterface[]>([]);
  const filteredAcronymsList = useRef<AcronymsInterface[]>([]);

  interface DecoratedAcronym extends AcronymsInterface {
    acronym_dec: string;
  }

  useEffect(() => {
    initializeState(
      paginationModel,
      setPaginationModel,
      setRowsPerPageOptions,
      viewAllClicked,
      acronymsList.length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acronymsList, viewAllClicked]);

  const xGridColumns: GridColDef[] = [
    {
      field: 'Acronym',
      editable: false,
      sortable: true,
      minWidth: 140,
      renderCell: (params) => (
        <span className="acronym-cell">{params.row.Acronym}</span>
      )
    },
    {
      field: 'Meaning',
      editable: false,
      sortable: true,
      minWidth: 300
    },
    {
      field: 'Details',
      editable: false,
      sortable: true,
      flex: 1,
      cellClassName: 'details-cell',
      renderCell: (params) => (params.row.Details ? params.row.Details : '-')
    }
  ];

  const CustomNoRowsOverlay = () => {
    return (
      <div className="no-rows">
        <i className="icon-error-warning" />
        <div>{t('acronyms.noData')}</div>
      </div>
    );
  };

  function renderOption(props, option) {
    return (
      <Typography
        {...props}
        key={option.id}
        style={{ fontSize: '1.4rem', display: 'block' }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.acronym_dec) }}
      ></Typography>
    );
  }

  const onChange = (e) => {
    const newValue = e.target.value;
    if (!newValue) {
      setAutocompleteText('');
    }
    // eslint-disable-next-line
    else if (checkForAlphabeticChars(newValue)) {
      setAutocompleteText(newValue);
      if (newValue.trim().length >= 1) {
        searchAcronyms(newValue.trim());
      }
    }
  };

  function onSearch() {
    setAutocompleteOpen(false);
    if (autocompleteText.length) {
      setAcronymsList(AcronymsList);
    }
    if (filteredAcronymsList.current.length === 0) {
      setAcronymsList([]);
      return;
    }
    setSelectedLetter('all');
    setAcronymsList(filteredAcronymsList.current);
  }

  function onClear() {
    setAutocompleteText('');
    setAutocompleteOpen(false);
    setSelectedLetter('all');
    setAcronymsList(AcronymsList);
    filteredAcronymsList.current = AcronymsList;
  }

  function handleAutocompleteOnChange(
    event: SyntheticEvent<Element, Event>,
    value
  ): void {
    filteredAcronymsList.current = makeFilteredList(
      (acronym: AcronymsInterface) => acronym.Acronym === value.Acronym
    );

    setSelectedLetter('all');
    setAutocompleteText(value.Acronym);
    setAcronymsList(filteredAcronymsList.current);
    setAutocompleteOpen(false);
  }

  function makeFilteredList(
    condition: (acronym: AcronymsInterface) => boolean
  ) {
    return AcronymsList.filter(condition);
  }

  function searchAcronyms(query: string) {
    const words = query
      .split(/\s+/)
      .filter((word) => word.length >= 1)
      .map((word) => `${word}`);

    if (words.length === 0) return [];
    const regexPattern = new RegExp(`(${words.join('|')})`, 'gi');
    const options: DecoratedAcronym[] = [];

    filteredAcronymsList.current = makeFilteredList(
      (acronym: AcronymsInterface) => {
        let value = regexPattern.test(acronym.Acronym);
        if (value) {
          options.push({
            ...acronym,
            acronym_dec: acronym.Acronym.replace(
              regexPattern,
              (match) => emphasizeMatch(match)
            )
          });
        }
        return value;
      }
    );
    setOptions(options);
  }

  function filterFromStartLetter(letter: string): void {
    setSelectedLetter(letter);
    setAutocompleteText('');
    if (letter === 'all') {
      setAcronymsList(AcronymsList);
    } else if (letter === '#') {
      setAcronymsList(
        AcronymsList.filter((acronym) =>
          /[0-9]/.test(acronym.Acronym.charAt(0))
        )
      );
    } else {
      const filteredAcronyms = AcronymsList.filter((acronym) =>
        acronym.Acronym.toLowerCase().startsWith(letter.toLowerCase())
      );
      setAcronymsList(filteredAcronyms);
    }
  }

  return (
    <div className="acronyms-container">
      <div className="acronyms-header-container">
        <div className="acronyms-header">
          <div className="acronyms-title">{t('acronyms.title')}</div>
        </div>
      </div>

      <AcBackToTopButton />
      <div className="acronyms-body">
        <div className="acronyms-left-section">
          <div className="search-bar">
            <Autocomplete
              open={autocompleteOpen}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  const newValue = replaceSpecialChars(value);
                  if (newValue.trim().length < 1) {
                    if (autocompleteOpen) setAutocompleteOpen(false);
                  } else {
                    if (!autocompleteOpen) setAutocompleteOpen(true);
                  }
                }
              }}
              filterOptions={(x) => x}
              autoComplete
              getOptionLabel={(option) => option.Acronym}
              inputValue={autocompleteText}
              onClose={() => {
                setAutocompleteOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearch();
                }
              }}
              className={autocompleteStyle.autocomplete}
              onChange={(event, value) =>
                handleAutocompleteOnChange(event, value)
              }
              noOptionsText={
                <Typography
                  style={{
                    fontSize: '1.4rem',
                    display: 'block',
                    color: 'black'
                  }}
                >
                  {t('faqs.noOptionsText')}
                </Typography>
              }
              options={options}
              slotProps={{ popper: { className: 'autocomplete-popper' } }}
              popupIcon={null}
              value={null}
              renderOption={(props, option) => renderOption(props, option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={autocompleteStyle.textField}
                  label="Search for a keyword or phrase"
                  onChange={onChange}
                  InputLabelProps={{
                    className: autocompleteStyle.textFieldLabel
                  }}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    inputProps: {
                      ...params.inputProps,
                      maxLength: 40,
                      style: { paddingLeft: 0 }
                    }
                  }}
                />
              )}
            />
            <div className="search-buttons">
              <Button
                variant="outlined"
                onClick={onSearch}
                className="search-btn"
              >
                {t('faqs.searchButton')}
              </Button>
              <Button
                variant="outlined"
                onClick={onClear}
                className="cancel-btn"
              >
                {t('faqs.clearButton')}
              </Button>
            </div>
          </div>
          <div className="letter-filter-section">
            <h2 className="letter-filter-title">
              {t('acronyms.letterFilterTitle')}
            </h2>
            <div className="letter-filter-links">
              <Grid container rowGap={2}>
                <Grid
                  item
                  key={'ALL'}
                  className={selectedLetter === 'all' ? 'letter-selected' : ''}
                  onClick={() => filterFromStartLetter('all')}
                >
                  <Typography
                    variant="h6"
                    className={`letter-filter-link ${selectedLetter === 'all' ? 'letter-link-selected' : ''}`}
                  >
                    All
                  </Typography>
                </Grid>
                <Grid
                  item
                  key={'#'}
                  className={selectedLetter === '#' ? 'letter-selected' : ''}
                  onClick={() => filterFromStartLetter('#')}
                >
                  <Typography
                    variant="h6"
                    className={`letter-filter-link ${selectedLetter === '#' ? 'letter-link-selected' : ''}`}
                  >
                    #
                  </Typography>
                </Grid>
                {Array.from({ length: 26 }, (_, i) =>
                  String.fromCharCode(65 + i)
                ).map((letter) => (
                  <Grid
                    item
                    key={letter}
                    onClick={() => filterFromStartLetter(letter)}
                    className={
                      selectedLetter === letter ? 'letter-selected' : ''
                    }
                  >
                    <Typography
                      variant="h6"
                      className={`letter-filter-link ${selectedLetter === letter ? 'letter-link-selected' : ''}`}
                    >
                      {letter}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </div>
          </div>
          <div className="multisort-instruction">
            {t('dbm.multiSortInstruction')}
          </div>
          <div className="acronyms-list-section">
            <h3 className="acronyms-list">{t('acronyms.acronymsList')}</h3>
            <a
              download
              href={`/acronyms/myACcess - Acronyms  (v1.0.1).xlsx`}
              className="export-files-section"
            >
              <i className="icon-Export-Icon"></i>
              <p className="export-files">{t('acronyms.exportList')}</p>
            </a>
          </div>
          <div className="acronyms-table">
            <DataGridPro
              autoHeight
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                pagination: (props: any) => (
                  <>
                    {paginationModel.pageSize < acronymsList.length ? (
                      <div className="enabled-view-all">
                        <span
                          onClick={() =>
                            showAllRows(
                              setViewAllClicked,
                              acronymsList.length,
                              paginationModel,
                              setPaginationModel
                            )
                          }
                        >
                          View All
                        </span>
                      </div>
                    ) : acronymsList.length > 10 ? (
                      <div className="disabled-view-all">
                        <span>View All</span>
                      </div>
                    ) : (
                      <div className="disabled-view-all-adjusted">
                        <span>View All</span>
                      </div>
                    )}
                    <GridPagination />
                  </>
                )
              }}
              disableColumnMenu
              disableRowSelectionOnClick
              //disableSelectionOnClick
              //disableExtendRowFullWidth={true}
              // pageSize={pageSize}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={rowsPerPageOptions}
              pagination
              getRowId={(r) => r.id}
              rows={acronymsList}
              columns={xGridColumns}
            />
          </div>
        </div>
        <div className="acronyms-right-section">
          <SupportPageHelpSection />
        </div>
      </div>
    </div>
  );
}

export default Acronyms;
